// File: e1c/src/components/content_editor/OpenTextBlock.js

import React, { useCallback, useMemo } from 'react';
import { Paper, TextField, IconButton, Grid, Typography, Box } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MediaAttachmentManager from './MediaAttachmentManager';
import PatientQuestionRenderer from './PatientQuestionRenderer'; 

const OpenTextBlock = React.memo(({ id, question, options = {}, onChange, onDelete, isInfo = false }) => {

    const memoizedOptions = useMemo(() => ({
        placeholder: options.placeholder || '',
        rows: options.rows ?? 3,
        media_assets: options.media_assets || [] 
    }), [options]);

    const handleQuestionChange = useCallback((event) => {
        if (onChange) { onChange({ question: event.target.value }); }
    }, [onChange]);

    const handleOptionChange = useCallback((optionName, value) => {
        if (onChange) {
            onChange({ options: { [optionName]: value } });
        }
    }, [onChange]);

    const handleDeleteClick = useCallback(() => {
        if (onDelete) { onDelete(id); }
    }, [id, onDelete]);

    const previewDefinition = useMemo(() => ({
        id,
        question,
        type: 'open_text',
        options: memoizedOptions
    }), [id, question, memoizedOptions]);

    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, position: 'relative', '&:hover .delete-button': { opacity: 1 } }}>
            <IconButton
                className="delete-button" aria-label="delete block" onClick={handleDeleteClick} size="small"
                sx={{ position: 'absolute', top: 8, right: 8, color: 'grey.500', opacity: 0.5, transition: 'opacity 0.2s', '&:hover': { opacity: 1, color: 'error.main' } }}
            >
                <DeleteOutlineIcon fontSize="small"/>
            </IconButton>
            
            <TextField
                fullWidth variant="outlined" size="small"
                label={isInfo ? `Informational Text (ID: ${id})` : `Open Text Question (ID: ${id})`}
                multiline minRows={1} value={question || ''}
                onChange={handleQuestionChange}
                placeholder={isInfo ? "Enter the informational text..." : "Enter the question text for the open text input..."}
                sx={{ mb: 2 }}
            />
            {!isInfo && (
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={8}>
                        <TextField
                            fullWidth variant="outlined" size="small"
                            label="Placeholder Text (for patient input)"
                            value={memoizedOptions.placeholder}
                            onChange={(e) => handleOptionChange('placeholder', e.target.value)}
                            placeholder="e.g., Type your detailed answer here..."
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth type="number" variant="outlined" size="small" label="Input Rows"
                            value={memoizedOptions.rows ?? ''}
                            onChange={(e) => handleOptionChange('rows', e.target.value === '' ? 3 : parseInt(e.target.value, 10))}
                            inputProps={{ min: 1, step: 1 }}
                            helperText="Lines for patient's answer"
                        />
                    </Grid>
                </Grid>
            )}
            <MediaAttachmentManager
                mediaAssets={memoizedOptions.media_assets}
                onChange={(newMedia) => handleOptionChange('media_assets', newMedia.media_assets)}
            />

            {!isInfo && (
                <Box sx={{ mt: 2, p: 1.5, border: '1px dashed lightgrey', borderRadius: 1, backgroundColor: 'grey.50' }}>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>
                        Patient View Preview (Open Text Input):
                    </Typography>
                    <PatientQuestionRenderer questionDefinition={previewDefinition} />
                </Box>
            )}

        </Paper>
    );
});

export default OpenTextBlock;